import React, { useState, useEffect } from 'react';
import axios from 'axios';

const COLORS = {
  primary: "#0760ad",
  primaryLight: "#098a48",
  secondary: "#118a94",
  white: "#FFFFFF"
};

const ServicesHeroModal = ({ isOpen, onClose, initialData }) => {
  const [servicesData, setServicesData] = useState({
    heroSection: {
      title: "",
      subtitle: "",
      backgroundGradient: "from-[#0760ad] to-[#098a48]",
      image: ""
    },
    descriptionSection: {
      text: ""
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const GRADIENT_OPTIONS = [
    { value: "from-[#0760ad] to-[#098a48]", label: "Blue to Green" },
    { value: "from-[#0d4b7a] to-[#118a94]", label: "Dark Blue to Teal" },
    { value: "from-[#1e3a8a] to-[#1e40af]", label: "Navy to Blue" },
    { value: "from-[#065f46] to-[#047857]", label: "Dark Green to Emerald" }
  ];

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/services/hero/`);
          if (response.ok) {
            const data = await response.json();
            setServicesData(data);
          }
        } catch (error) {
          console.error('Error fetching services hero data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setServicesData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setServicesData(prev => ({ 
      ...prev,
      [name.split('.')[0]]: {
        ...prev[name.split('.')[0]],
        [name.split('.')[1]]: value
      }
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'services_hero');

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      const imageUrl = response.data.image?.url || response.data.image;
      if (imageUrl) {
        setServicesData(prev => ({
          ...prev,
          heroSection: {
            ...prev.heroSection,
            image: imageUrl
          }
        }));
      } else {
        throw new Error('Invalid image URL in response');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/services/hero/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(servicesData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update services hero data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating services hero data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading services hero data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Services Hero Section</h3>
        
        <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-6">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Hero Title</span>
            </label>
            <input 
              type="text" 
              name="heroSection.title"
              value={servicesData.heroSection?.title || ''} 
              onChange={handleChange} 
              className="input input-bordered w-full" 
            />
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Hero Subtitle</span>
            </label>
            <input 
              type="text" 
              name="heroSection.subtitle"
              value={servicesData.heroSection?.subtitle || ''} 
              onChange={handleChange} 
              className="input input-bordered w-full" 
            />
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Background Gradient</span>
            </label>
            <select 
              name="heroSection.backgroundGradient"
              value={servicesData.heroSection?.backgroundGradient || GRADIENT_OPTIONS[0].value} 
              onChange={handleChange} 
              className="select select-bordered w-full"
            >
              {GRADIENT_OPTIONS.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Hero Background Image (optional)</span>
            </label>
            <div className="flex items-center gap-2">
              {servicesData.heroSection?.image && (
                <div className="avatar">
                  <div className="w-12 rounded">
                    <img src={servicesData.heroSection.image} alt="Hero preview" />
                  </div>
                </div>
              )}
              <input 
                type="file" 
                accept="image/*"
                onChange={handleImageUpload}
                className="file-input file-input-bordered w-full max-w-xs"
                disabled={isUploading}
              />
              {isUploading && (
                <progress 
                  className="progress progress-primary w-20" 
                  value={uploadProgress} 
                  max="100"
                ></progress>
              )}
            </div>
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Description Text</span>
            </label>
            <textarea 
              name="descriptionSection.text"
              value={servicesData.descriptionSection?.text || ''} 
              onChange={handleChange} 
              className="textarea textarea-bordered w-full h-32" 
            />
          </div>
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ServicesHeroModal;